import React, { useState, useEffect } from 'react';
import {
  IonContent,
} from '@ionic/react';
import {
  Box,
  Stack,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import useFetch from 'use-http';
import PageWithBackButton from 'components/generic/PageWithBackButton';
import './GoalsCreate.css';
import PillRenderer from 'common/molecules/pillRenderer/PillRenderer';
import BottomDrawer from 'common/molecules/BottomDrawer/BottomDrawer';
import InputField from 'common/atoms/inputField/InputField';
import BackrsButton from 'common/atoms/backrsButton/BackrsButton';
import appColors from 'common/app_colors';
import appStrings from 'common/app_strings';
import PropTypes from 'prop-types';
import { goalTypeButtons } from './utils';

const { goals: { goalsSubtype: STRINGS }, common } = appStrings;

const GoalsSubtype = ({ onComplete, onBack, goalData }) => {
  const [goalSubtypes, setGoalSubtypes] = useState([]);
  const [responseObject, setResponseObject] = useState(null);
  const [selectedSubtypes, setSelectedSubtypes] = useState(goalData?.subtypes?.map((subtype) => subtype.name) || []);
  const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState(false);
  const [otherSubtype, setOtherSubtype] = useState('');

  const { get: doGet } = useFetch();
  const goalTypeButton = goalTypeButtons.find((button) => button.key === goalData?.type);

  const getGoalSubtype = async () => {
    const response = await doGet(`/goals/subtypes/${goalData?.type}`);
    const subtypes = response?.map((subtype) => subtype.name);
    setResponseObject(response);
    const goalSubtypesToSet = subtypes ? [...subtypes, 'Other'] : ['Other'];
    setGoalSubtypes(goalSubtypesToSet);
  };

  const handleOtherPillClick = () => {
    setIsBottomDrawerOpen(!isBottomDrawerOpen);
    setSelectedSubtypes([...selectedSubtypes, 'Other']);
  };

  const handlePillClick = (subtype) => {
    if (subtype === 'Other') {
      handleOtherPillClick();
    } else if (selectedSubtypes.includes(subtype)) {
      setSelectedSubtypes(selectedSubtypes.filter((selectedSubtype) => selectedSubtype !== subtype));
    } else {
      setSelectedSubtypes([...selectedSubtypes, subtype]);
    }
  };

  const handleSaveSelectedGoalSubtypes = async () => {
    const formattedSubtypes = selectedSubtypes.map((subtype) => {
      if (subtype === 'Other') {
        return {
          name: 'Other',
          id: null,
        };
      }
      return {
        subtype_id: responseObject?.find((goalSubtype) => goalSubtype.name === subtype)?.id,
        name: subtype,
      };
    });
    onComplete({ subtypes: formattedSubtypes });
  };

  const handleSubtypeSave = () => {
    if (otherSubtype === '') {
      setSelectedSubtypes(selectedSubtypes.filter((subtype) => subtype !== 'Other'));
    } else {
      setSelectedSubtypes([...selectedSubtypes, otherSubtype]);
    }
    setIsBottomDrawerOpen(false);
  };

  useEffect(() => {
    void (async () => {
    await getGoalSubtype();
  })();
  }, [goalData?.type]);

  if (!goalData.type) {
    return null;
  }

  return (
    <PageWithBackButton backButtonText="New Goal" onBack={onBack}>
      <IonContent fullscreen>
        <Box
          mx={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <Typography
              variant="h4"
              fontSize={20}
              fontWeight={700}
              sx={{ textAlign: 'center', mt: 3.5, mb: 4 }}
            >
              {STRINGS.title(goalTypeButton.key)}
            </Typography>
            <button
              key={goalTypeButton.key}
              type="button"
              className={clsx('goals-type-button', goalTypeButton.classname)}
              style={{ backgroundColor: goalTypeButton.bgColor }}
            >
              <img
                src={goalTypeButton.icon}
                className="goals-type-button-icon"
                alt={`${goalTypeButton.key} goal icon`}
              />
              <Typography
                variant="h4"
                fontWeight={700}
                color="#fff"
                fontSize={24}
                width="100%"
              >
                {goalTypeButton.title}
              </Typography>
            </button>
            <Stack mt={2}>
              <PillRenderer
                items={goalSubtypes}
                activeItems={selectedSubtypes}
                pillStyles={{ backgroundColor: appColors.pills.white }}
                handlePillClick={handlePillClick}
              />
            </Stack>
          </Box>
          <BackrsButton
            label={common.saveAndContinue}
            onClick={handleSaveSelectedGoalSubtypes}
            buttonStylesOverride={{ backgroundColor: appColors.buttons.profileSaveButton, marginBottom: '24px' }}
          />
        </Box>
        <BottomDrawer
          open={isBottomDrawerOpen}
          toggleDrawer={setIsBottomDrawerOpen}
          title={STRINGS.drawerTitle}
          handleCTAClick={handleSubtypeSave}
          ctaLabel={common.save}
        >
          <InputField
            onChange={(e) => setOtherSubtype(e.target.value)}
            placeholder={STRINGS.placeholder}
            fieldValue={otherSubtype}
          />
        </BottomDrawer>
      </IonContent>
    </PageWithBackButton>
  );
};

GoalsSubtype.propTypes = {
  onComplete: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  goalData: PropTypes.shape({
    type: PropTypes.string.isRequired,
  }).isRequired,
};

export default GoalsSubtype;
